import React from 'react';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import {
  canShowAlcohol68,
  canShowAlcohol70,
  canShowAlcohol72,
  resolveDisplaySettings
} from '../utils/displaySettings';
import './Charts.css';

const COLORS = {
  primary: '#3F9AAE',
  secondary: '#0F2854',
  success: '#10b981',
  danger: '#ef4444',
  warning: '#f59e0b',
  info: '#6366f1',
  purple: '#8b5cf6'
};

const Charts = ({ 
  readingsOverTime, 
  clientWiseReadings, 
  managerWiseReadings, 
  employeeWiseReadings,
  adulterationOverTime,
  alcoholTrendsOverTime,
  devicesOverTime,
  qualityDistribution,
  morningEveningComparison
}) => {
  const { user } = useAuth();
  const displaySettings = resolveDisplaySettings(user);
  const show68 = canShowAlcohol68(displaySettings);
  const show70 = canShowAlcohol70(displaySettings);
  const show72 = canShowAlcohol72(displaySettings);
  const hasReadingsOverTime = readingsOverTime && readingsOverTime.length > 0;
  const hasClientWiseReadings = clientWiseReadings && clientWiseReadings.length > 0;
  const hasManagerWiseReadings = managerWiseReadings && managerWiseReadings.length > 0;
  const hasEmployeeWiseReadings = employeeWiseReadings && employeeWiseReadings.length > 0;
  const hasAdulterationOverTime = adulterationOverTime && adulterationOverTime.length > 0;
  const hasAlcoholTrendsOverTime = alcoholTrendsOverTime && alcoholTrendsOverTime.length > 0;
  const hasDevicesOverTime = devicesOverTime && devicesOverTime.length > 0;
  const hasQualityDistribution = qualityDistribution && qualityDistribution.length > 0;
  const hasMorningEveningComparison = morningEveningComparison && morningEveningComparison.length > 0;

  const hasAnyChart = hasReadingsOverTime || hasClientWiseReadings || hasManagerWiseReadings || 
    hasEmployeeWiseReadings || hasAdulterationOverTime || hasAlcoholTrendsOverTime || 
    hasDevicesOverTime || hasQualityDistribution || hasMorningEveningComparison;

  if (!hasAnyChart) {
    return (
      <div className="charts-container">
        <div className="chart-card">
          <h2>Charts</h2>
          <div className="empty-chart-message">
            <p>No chart data available yet. Start adding readings to see visualizations.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="charts-container">
      {hasReadingsOverTime && (
        <div className="chart-card">
          <h2>Readings Over Time</h2>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={readingsOverTime}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis 
                dataKey="date" 
                stroke="#495057"
                style={{ fontSize: '0.6875rem' }}
              />
              <YAxis 
                stroke="#495057"
                style={{ fontSize: '0.6875rem' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#ffffff',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '0.8125rem',
                  color: '#0F2854'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '0.8125rem', color: '#495057' }}
              />
              <Line 
                type="monotone" 
                dataKey="count" 
                stroke="#3F9AAE" 
                strokeWidth={3}
                dot={{ fill: '#3F9AAE', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {hasClientWiseReadings && (
        <div className="chart-card">
          <h2>Client-wise Readings</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={clientWiseReadings}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis 
                dataKey="clientName" 
                stroke="#495057"
                style={{ fontSize: '0.75rem' }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                stroke="#495057"
                style={{ fontSize: '0.75rem' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#ffffff',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  color: '#0F2854'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '0.875rem', color: '#495057' }}
              />
              <Bar 
                dataKey="readingCount" 
                fill="#0F2854"
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {hasManagerWiseReadings && (
        <div className="chart-card">
          <h2>Manager-wise Readings</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={managerWiseReadings}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis 
                dataKey="managerName" 
                stroke="#495057"
                style={{ fontSize: '0.75rem' }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                stroke="#495057"
                style={{ fontSize: '0.75rem' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#ffffff',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  color: '#0F2854'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '0.875rem', color: '#495057' }}
              />
              <Bar 
                dataKey="readingCount" 
                fill="#6366f1"
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {hasEmployeeWiseReadings && (
        <div className="chart-card">
          <h2>Employee-wise Readings</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={employeeWiseReadings}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis 
                dataKey="employeeName" 
                stroke="#495057"
                style={{ fontSize: '0.75rem' }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                stroke="#495057"
                style={{ fontSize: '0.75rem' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#ffffff',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  color: '#0F2854'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '0.875rem', color: '#495057' }}
              />
              <Bar 
                dataKey="readingCount" 
                fill="#10b981"
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {hasAdulterationOverTime && (
        <div className="chart-card">
          <h2>Adulteration Trends Over Time</h2>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={adulterationOverTime}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis 
                dataKey="date" 
                stroke="#495057"
                style={{ fontSize: '0.6875rem' }}
              />
              <YAxis 
                stroke="#495057"
                style={{ fontSize: '0.6875rem' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#ffffff',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '0.8125rem',
                  color: '#0F2854'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '0.8125rem', color: '#495057' }}
              />
              <Line 
                type="monotone" 
                dataKey="adulterated" 
                stroke={COLORS.danger} 
                strokeWidth={3}
                dot={{ fill: COLORS.danger, r: 4 }}
                activeDot={{ r: 6 }}
                name="Adulterated"
              />
              <Line 
                type="monotone" 
                dataKey="non_adulterated" 
                stroke={COLORS.success} 
                strokeWidth={3}
                dot={{ fill: COLORS.success, r: 4 }}
                activeDot={{ r: 6 }}
                name="Non-Adulterated"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {hasAlcoholTrendsOverTime && (
        <div className="chart-card">
          <h2>Alcohol Percentage Trends Over Time</h2>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={alcoholTrendsOverTime}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis 
                dataKey="date" 
                stroke="#495057"
                style={{ fontSize: '0.6875rem' }}
              />
              <YAxis 
                stroke="#495057"
                style={{ fontSize: '0.6875rem' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#ffffff',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '0.8125rem',
                  color: '#0F2854'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '0.8125rem', color: '#495057' }}
              />
              <Line 
                type="monotone" 
                dataKey="60_pass" 
                stroke="#14b8a6" 
                strokeWidth={2}
                dot={{ fill: '#14b8a6', r: 3 }}
                name="60% Pass"
              />
              <Line 
                type="monotone" 
                dataKey="60_fail" 
                stroke="#0d9488" 
                strokeWidth={2}
                dot={{ fill: '#0d9488', r: 3 }}
                strokeDasharray="5 5"
                name="60% Fail"
              />
              <Line 
                type="monotone" 
                dataKey="65_pass" 
                stroke="#06b6d4" 
                strokeWidth={2}
                dot={{ fill: '#06b6d4', r: 3 }}
                name="65% Pass"
              />
              <Line 
                type="monotone" 
                dataKey="65_fail" 
                stroke="#0891b2" 
                strokeWidth={2}
                dot={{ fill: '#0891b2', r: 3 }}
                strokeDasharray="5 5"
                name="65% Fail"
              />
              {show68 && (
              <Line 
                type="monotone" 
                dataKey="68_pass" 
                stroke={COLORS.success} 
                strokeWidth={2}
                dot={{ fill: COLORS.success, r: 3 }}
                name="68% Pass"
              />
              )}
              {show68 && (
              <Line 
                type="monotone" 
                dataKey="68_fail" 
                stroke={COLORS.danger} 
                strokeWidth={2}
                dot={{ fill: COLORS.danger, r: 3 }}
                strokeDasharray="5 5"
                name="68% Fail"
              />
              )}
              {show70 && (
              <Line 
                type="monotone" 
                dataKey="70_pass" 
                stroke={COLORS.info} 
                strokeWidth={2}
                dot={{ fill: COLORS.info, r: 3 }}
                name="70% Pass"
              />
              )}
              {show70 && (
              <Line 
                type="monotone" 
                dataKey="70_fail" 
                stroke="#f97316" 
                strokeWidth={2}
                dot={{ fill: '#f97316', r: 3 }}
                strokeDasharray="5 5"
                name="70% Fail"
              />
              )}
              {show72 && (
              <Line 
                type="monotone" 
                dataKey="72_pass" 
                stroke={COLORS.purple} 
                strokeWidth={2}
                dot={{ fill: COLORS.purple, r: 3 }}
                name="72% Pass"
              />
              )}
              {show72 && (
              <Line 
                type="monotone" 
                dataKey="72_fail" 
                stroke="#ec4899" 
                strokeWidth={2}
                dot={{ fill: '#ec4899', r: 3 }}
                strokeDasharray="5 5"
                name="72% Fail"
              />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {hasDevicesOverTime && (
        <div className="chart-card">
          <h2>Active Devices Over Time</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={devicesOverTime}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis 
                dataKey="date" 
                stroke="#495057"
                style={{ fontSize: '0.6875rem' }}
              />
              <YAxis 
                stroke="#495057"
                style={{ fontSize: '0.6875rem' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#ffffff',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '0.8125rem',
                  color: '#0F2854'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '0.8125rem', color: '#495057' }}
              />
              <Bar 
                dataKey="count" 
                fill={COLORS.primary}
                radius={[8, 8, 0, 0]}
                name="Unique Devices"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {hasQualityDistribution && (
        <div className="chart-card">
          <h2>Sample Quality Distribution</h2>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={qualityDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {qualityDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.name === 'Adulterated' ? COLORS.danger : COLORS.success} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#ffffff',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  color: '#0F2854'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '0.875rem', color: '#495057' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {hasMorningEveningComparison && (
        <div className="chart-card">
          <h2>Morning vs Evening Comparison</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={morningEveningComparison}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
              <XAxis 
                dataKey="name" 
                stroke="#495057"
                style={{ fontSize: '0.75rem' }}
              />
              <YAxis 
                stroke="#495057"
                style={{ fontSize: '0.75rem' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#ffffff',
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  color: '#0F2854'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '0.875rem', color: '#495057' }}
              />
              <Bar 
                dataKey="value" 
                fill={COLORS.warning}
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default Charts;

